import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  IconButton,
  Menu,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Pagination,
  Snackbar,
  Chip,
} from "@mui/material";
import {
  CloudDownload as CloudDownloadIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  AccessTime as AccessTimeIcon,
  Storage as StorageIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../auth";
import ListLayout from "../../../../components/Layout/ListLayout";
import {
  useListStorageReports,
  useGenerateAndDownloadStorageReport,
  downloadStorageReportCsv,
} from "../../../../api/bpm/endpoints";
import { StorageUsageReportDto } from "../../../../api/bpm/model/storageUsageReportDto";
import {
  MONTHS,
  formatBytes,
  formatDateTime,
  downloadCsvFile,
} from "../reports";
import { PageStorageUsageReportDto } from "../../../../api/bpm/model/pageStorageUsageReportDto";

const START_YEAR = 2026;

export const Route = createFileRoute("/__auth/admin/reports/")({
  component: ReportsComponent,
  staticData: {
    title: "sidebar.reports",
  },
});

function ReportsComponent() {
  const { t, i18n } = useTranslation();
  const { currentTenant } = useAuth();
  const effectiveTenant = currentTenant || "default-domain";
  const navigate = useNavigate();

  const getMonthLabel = useCallback(
    (monthVal: number) => {
      const found = MONTHS.find((m) => m.value === monthVal);
      return found ? t(`reports.months.${found.key}`) : String(monthVal);
    },
    [t]
  );

  const currentYear = new Date().getFullYear();
  const availableYears = useMemo(() => {
    const years = [];
    for (let yr = START_YEAR; yr < currentYear; yr++) {
      years.push(yr);
    }
    return years;
  }, [currentYear]);

  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(
    availableYears.length > 0 ? availableYears[0] : START_YEAR
  );

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage] = useState<number>(10);

  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeReport, setActiveReport] = useState<StorageUsageReportDto | null>(null);

  const {
    data: apiData,
    isLoading: isLoadingHistory,
    refetch: refetchHistory,
  } = useListStorageReports(effectiveTenant, {
    page,
    size: rowsPerPage,
  });

  const generateMutation = useGenerateAndDownloadStorageReport();

  const reportsData = apiData as PageStorageUsageReportDto | undefined;
  const totalPages = reportsData?.totalPages || 0;

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, report: StorageUsageReportDto) => {
    setAnchorEl(event.currentTarget);
    setActiveReport(report);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveReport(null);
  };

  const handleGenerateReport = async () => {
    try {
      setToast((prev) => ({ ...prev, open: false }));
      const response = await generateMutation.mutateAsync({
        tenant: effectiveTenant,
        data: {
          month: selectedMonth,
          year: selectedYear,
        },
      });

      const csvData = typeof response === "string"
        ? response
        : (response && typeof response === "object" && "data" in response)
          ? (response as any).data
          : null;

      if (csvData) {
        const monthLabel = getMonthLabel(selectedMonth);
        const filename = `relatorio_consumo_${monthLabel}_${selectedYear}.csv`;
        downloadCsvFile(filename, csvData);
        setToast({
          open: true,
          severity: "success",
          message: t("reports.generateSuccess"),
        });
        refetchHistory();
      } else {
        setToast({
          open: true,
          severity: "error",
          message: t("reports.generateEmptyError"),
        });
      }
    } catch (err) {
      setToast({
        open: true,
        severity: "error",
        message: t("reports.generateError"),
      });
    }
  };

  const handleDownloadReport = async (report: StorageUsageReportDto) => {
    try {
      setToast((prev) => ({ ...prev, open: false }));
      const monthLabel = report.month ? getMonthLabel(report.month) : String(report.month);
      const filename = `relatorio_consumo_${monthLabel}_${report.year}.csv`;

      const response = await downloadStorageReportCsv(effectiveTenant, report.id!);

      const csvData = typeof response === "string"
        ? response
        : (response && typeof response === "object" && "data" in response)
          ? (response as any).data
          : null;

      if (csvData) {
        downloadCsvFile(filename, csvData);
        setToast({
          open: true,
          severity: "success",
          message: t("reports.downloadSuccess"),
        });
      } else {
        setToast({
          open: true,
          severity: "error",
          message: t("reports.downloadEmptyError"),
        });
      }
    } catch (err) {
      setToast({
        open: true,
        severity: "error",
        message: t("reports.downloadError"),
      });
    }
  };

  const handleViewReport = (report: StorageUsageReportDto) => {
    handleMenuClose();
    navigate({
      to: "/admin/reports/$reportId",
      params: { reportId: String(report.id) },
    });
  };

  return (
    <ListLayout
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <StorageIcon sx={{ fontSize: { xs: "1.75rem", sm: "2.25rem" }, color: "primary.main" }} />
          <span>{t("reports.title")}</span>
        </Box>
      }
      subtitle={t("reports.subtitle")}
    >

      <Grid container spacing={3}>
        <Grid size={12}>
          <Card sx={{ borderRadius: 3, boxShadow: (theme) => theme.shadows[2], p: 1 }}>
            <CardHeader
              title={t("reports.generateTitle")}
              titleTypographyProps={{ fontWeight: "bold" }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, color: "text.primary" }}>
                    {t("reports.month")}
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(Number(e.target.value))}
                      sx={{
                        borderRadius: 2,
                        bgcolor: "action.hover",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "divider",
                        },
                      }}
                    >
                      {MONTHS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          {t(`reports.months.${m.key}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, color: "text.primary" }}>
                    {t("reports.year")}
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                      sx={{
                        borderRadius: 2,
                        bgcolor: "action.hover",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "divider",
                        },
                      }}
                    >
                      {availableYears.length > 0 ? (
                        availableYears.map((yr) => (
                          <MenuItem key={yr} value={yr}>
                            {yr}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value={selectedYear}>
                          {selectedYear}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={12}>
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(2, 132, 199, 0.15)" : "rgb(240, 249, 255)",
                      color: (theme) => theme.palette.mode === "dark" ? "rgb(125, 211, 252)" : "rgb(3, 105, 161)",
                      border: "1px solid",
                      borderColor: (theme) => theme.palette.mode === "dark" ? "rgba(2, 132, 199, 0.3)" : "rgb(186, 230, 253)",
                      py: 1.5,
                      "& .MuiAlert-message": {
                        width: "100%",
                      },
                    }}
                  >
                    {t("reports.generateInfo")}
                  </Alert>
                </Grid>

                <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={generateMutation.isPending}
                    startIcon={generateMutation.isPending ? <CircularProgress size={20} /> : <DownloadIcon />}
                    onClick={handleGenerateReport}
                    sx={{ px: 4, py: 1.5, borderRadius: 2, textTransform: "none", fontWeight: "bold" }}
                  >
                    {generateMutation.isPending ? t("reports.processing") : t("reports.downloadCsv")}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card sx={{ borderRadius: 3, boxShadow: (theme) => theme.shadows[2] }}>
            <CardHeader
              title={t("reports.historyTitle")}
              titleTypographyProps={{ fontWeight: "bold" }}
              action={
                reportsData?.totalElements !== undefined ? (
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: "1rem !important" }} />}
                    label={t("reports.recentBadge", { count: reportsData.totalElements })}
                    size="small"
                    sx={{
                      bgcolor: "action.hover",
                      fontWeight: 600,
                      borderRadius: 1.5,
                      px: 0.5,
                    }}
                  />
                ) : null
              }
            />
            <CardContent>
              {isLoadingHistory ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : reportsData?.content && reportsData.content.length > 0 ? (
                <>
                  <List disablePadding>
                    {reportsData.content.map((report) => {
                      const reportMonthLabel = report.month ? getMonthLabel(report.month) : String(report.month);
                      const sizeBytes = report.csvContent ? new Blob([report.csvContent]).size : undefined;
                      const formattedSize = sizeBytes !== undefined ? formatBytes(sizeBytes) : "";
                      const formattedDate = formatDateTime(report.createdAt, i18n.language);

                      const subtitle = report.createdBy
                        ? t("reports.historySecondaryWithUser", { date: formattedDate, user: report.createdBy })
                        : t("reports.historySecondaryWithoutUser", { date: formattedDate });

                      return (
                        <ListItem
                          key={report.id}
                          divider
                          sx={{
                            py: 2,
                            px: 1,
                            transition: "background-color 0.2s",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                          secondaryAction={
                            <IconButton edge="end" onClick={(e) => handleMenuOpen(e, report)}>
                              <MoreVertIcon />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {reportMonthLabel}/{report.year}
                                </Typography>
                                {formattedSize && (
                                  <Chip
                                    label={formattedSize}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      bgcolor: "action.hover",
                                      borderRadius: 1.5,
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={subtitle}
                          />
                        </ListItem>
                      );
                    })}
                  </List>

                  {totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                      <Pagination
                        count={totalPages}
                        page={page + 1}
                        onChange={(_, value) => setPage(value - 1)}
                        color="primary"
                        shape="rounded"
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">{t("reports.noHistory")}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} keepMounted>
        <MenuItem
          onClick={() => {
            if (activeReport) handleViewReport(activeReport);
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("reports.actionVisualize")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (activeReport) {
              handleDownloadReport(activeReport);
              handleMenuClose();
            }
          }}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("reports.actionDownload")}</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "8px", fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
